import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { Controller, useForm, useFormContext } from "react-hook-form";
import {
  FORM_VALIDATIONS,
  changePasswordSchema,
} from "constants/formValidation";
import CustomTextFiled from "components/CustomTextField";
import { Fragment, useRef, useState } from "react";
import OTPVerification from "components/general/auth/OTPVerification";
import { OTP_TYPE } from "constants/general";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import { sendOTP, setOtpVerification } from "slices/authReducers";
import VerifyMobileOtp from "./VerifyMobileOtp";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { useDispatch } from "react-redux";
import { handleSnackBar } from "slices/global";
const PasswordEndornment = ({ showPassword, setShowPassword }: any) => {
  const handleClickShowPassword = () => setShowPassword((show: any) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  return (
    <InputAdornment position="end" sx={{ mr: 2 }}>
      <IconButton
        aria-label="toggle password visibility"
        onClick={handleClickShowPassword}
        onMouseDown={handleMouseDownPassword}
        edge="end"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );
};

const ChangePassword = ({ showDialog, setShowDialog }: any) => {
  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(changePasswordSchema),
  });
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { authReducer }: any = useAppSelector((state) => state);
  const dispatch = useAppDispatch();
  const cancelHandler = () => {
    setShowDialog(false);
  };

  const savePassword = async () => {
    trigger(["confirm_new_password", "current_password", "new_password"]);
    if (isValid) {
      setLoading(true);
      const auth = await getAuth();
      const user: any = await auth.currentUser;
      const credential = EmailAuthProvider.credential(
        user?.email,
        getValues()?.current_password
      );
      reauthenticateWithCredential(user, credential)
        .then(async () => {
          await updatePassword(user, getValues().new_password);
          dispatch(
            handleSnackBar({
              open: true,
              message: "Success Password Changed",
              severity: "success",
            })
          );
          setLoading(false);
          cancelHandler();
        })
        .catch((error) => {
          setLoading(false);
          dispatch(
            handleSnackBar({
              open: true,
              message:
                error?.code == "auth/wrong-password"
                  ? "Incorrect credentials,Failed!"
                  : "Error occured, Please try again",
              severity: "error",
            })
          );
          console.log({ error });
        });
    }
  };

  return (
    <Box sx={{ p: 5 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontSize: "26px",
              fontWeight: "700",
              lineHeight: "31.47px",
              textAlign: "center",
            }}
          >
            Change Password
          </Typography>
        </Grid>
        

        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Controller
            control={control}
            name="current_password"
            defaultValue=""
            rules={{
              ...FORM_VALIDATIONS.REQUIRED_MOBILE,
              ...FORM_VALIDATIONS.maxLengthValidation(10),
              ...FORM_VALIDATIONS.minLengthValidation(10),
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CustomTextFiled
                inputValue={value}
                label="current password"
                onInputChange={onChange}
                showError={!!error}
                errorMessage={error?.message || " "}
                showEndDropDown={false}
                showVerifiedIconAtEnd={false}
                type={showCurrentPassword ? "text" : "password"}
                inputProps={{
                  endAdornment: (
                    <PasswordEndornment
                      showPassword={showCurrentPassword}
                      setShowPassword={setShowCurrentPassword}
                    />
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Controller
            control={control}
            name="new_password"
            defaultValue=""
            rules={{
              ...FORM_VALIDATIONS.REQUIRED_MOBILE,
              ...FORM_VALIDATIONS.maxLengthValidation(10),
              ...FORM_VALIDATIONS.minLengthValidation(10),
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CustomTextFiled
                inputValue={value}
                label="new password"
                onInputChange={onChange}
                showError={!!error}
                errorMessage={error?.message || " "}
                showEndDropDown={false}
                showVerifiedIconAtEnd={false}
                type={showNewPassword ? "text" : "password"}
                inputProps={{
                  endAdornment: (
                    <PasswordEndornment
                      showPassword={showNewPassword}
                      setShowPassword={setShowNewPassword}
                    />
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Controller
            control={control}
            name="confirm_new_password"
            defaultValue=""
            rules={{
              ...FORM_VALIDATIONS.REQUIRED_MOBILE,
              ...FORM_VALIDATIONS.maxLengthValidation(10),
              ...FORM_VALIDATIONS.minLengthValidation(10),
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CustomTextFiled
                inputValue={value}
                label="confirm new password"
                onInputChange={onChange}
                showError={!!error}
                errorMessage={error?.message || " "}
                showEndDropDown={false}
                showVerifiedIconAtEnd={false}
                type={showConfirmPassword ? "text" : "password"}
                inputProps={{
                  endAdornment: (
                    <PasswordEndornment
                      showPassword={showConfirmPassword}
                      setShowPassword={setShowConfirmPassword}
                    />
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={cancelHandler}
            disabled={loading}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={savePassword}
            disabled={
              getValues()?.confirm_new_password != getValues()?.new_password ||
              loading ||
              getValues()?.current_password == getValues()?.new_password
            }
            endIcon={loading ? <CircularProgress size={15} /> : null}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChangePassword;
